import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronRight,
  Compass,
  Copy,
  Eye,
  EyeOff,
  GitFork,
  Hexagon,
  Layers,
  type LucideIcon,
  MapPin,
  Palette,
  Radar,
  Search,
  Sparkles,
  Table2,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { ChangedBadge } from '@/components/entity-history/ChangedBadge'
import { classifyEntities } from '@/components/geolocation/entity-classification'
import { InsightCard } from '@/components/investigations/InsightCard'
import { investigationEvidence } from '@/components/knowledge-graph-view/face-visibility'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { AiInsightsPanel } from '@/features/ai-insights'
import { applyFactVisibility, isHiddenFact } from '@/features/analyst-facts/fact-marks'
import { useShowNoLongerValid } from '@/features/analyst-facts/useFactVisibility'
import { entityUid } from '@/features/entity-history/history-rows'
import { useChangedEntities } from '@/features/entity-history/useEntitySummaries'
import { defaultTrackColorFor, pastelFor } from '@/features/geolocation/utils/marker-palette'
import { resolveInsight } from '@/features/insights/utils/resolveInsight'
import { type Insight, insightView } from '@/features/insights/utils/types'
import { useInsights } from '@/features/insights/utils/useInsights'
import { useObjectSet } from '@/features/investigations/hooks/useObjectSet'
import {
  mapObjectSetId,
  snippetGraphNodes,
  snippetSelection,
} from '@/features/source-snippets/object-set'
import { ADVANCED_FACE_DETECTION_EXTENSION_ID, useExtensionEnabled } from '@/hooks/use-extensions'
import { isMobileViewport } from '@/hooks/use-mobile'
import { useCockpitStore } from '@/stores/cockpit-store'
import { useGeofenceStore } from '@/stores/geofence-store'
import type { GeoEntity } from '@/stores/geolocation-store'
import { useGeolocationStore } from '@/stores/geolocation-store'
import {
  useInvestigationsStore,
  useInvestigationsStoreApi,
} from '@/stores/investigations-store-provider'
import { useObjectSetContributionsStore } from '@/stores/object-set-contributions-store'
import { useSelectionStore } from '@/stores/selection-store'
import { useTimelineStore } from '@/stores/timeline-store'
import { cn } from '@/utils/cn'
import { getEntityDisplayLabel } from '@/utils/entity-display'
import { hasGeoCoordinates } from '@/utils/geo'
import type { InvestigationView } from './EmptyStateInsightsPrompt'
import { OpenIdentityLink } from './entity-detail/OpenIdentityLink'
import { SidebarEntityDetail } from './entity-detail/SidebarEntityDetail'
import { SidebarRelationshipDetail } from './entity-detail/SidebarRelationshipDetail'

const TRACK_COLOR_PALETTE = [
  '#ff5252',
  '#ff9800',
  '#fbc02d',
  '#a3e635',
  '#22c55e',
  '#14b8a6',
  '#00bfff',
  '#3b82f6',
  '#8b5cf6',
  '#d946ef',
  '#f472b6',
  '#94a3b8',
] as const

type TabId = 'objects' | 'insights'
type EntityFilter = 'all' | 'animated' | 'time-anchored' | 'static'
type SidebarSection = 'entities' | 'geofences' | 'relationships'

export interface InvestigationSidebarProps {
  caseId: string
  view: InvestigationView
  open: boolean
  onOpenChange: (open: boolean) => void
  className?: string
}

// Sidebar surface — uses the warmer `accent` token (not `card`) so the panel
// stands clearly off the bright `card` canvas it floats over. Heavy drop
// shadow + faint top-edge highlight reinforces elevation without blur tricks.
const SIDEBAR_SURFACE = cn(
  'border border-border bg-accent text-accent-foreground',
  'shadow-[0_24px_72px_-18px_hsl(var(--foreground)/0.40),0_6px_18px_-6px_hsl(var(--foreground)/0.22),inset_0_1px_0_hsl(0_0%_100%/0.08)]'
)

export function InvestigationSidebar({
  caseId,
  view,
  open,
  onOpenChange,
  className,
}: InvestigationSidebarProps) {
  // On a phone the sidebar overlays the whole canvas, so a host that mounts
  // it open would hide the view the user came for. Collapse it once on mount;
  // a selection or the host's open button brings it back. The gate keeps the
  // panel out of the phone's first pass, so it never flashes over the canvas
  // before the host has taken the collapse.
  const [phoneGate, setPhoneGate] = useState(isMobileViewport)
  useEffect(() => {
    if (!phoneGate) return
    onOpenChange(false)
    setPhoneGate(false)
  }, [phoneGate, onOpenChange])

  return (
    <AnimatePresence initial={false}>
      {open && !phoneGate && (
        <motion.aside
          key="investigation-sidebar"
          // Slim slide-in: opacity + x only. The previous scale + blur
          // animation gave the sidebar a heavier "morph in" feel which, when
          // triggered by a graph-canvas click that auto-opens the sidebar,
          // visually competed with the canvas and read as the graph itself
          // jittering. Pure translate is GPU-cheap and keeps neighbouring
          // pixels (the canvas behind) stable.
          initial={{ opacity: 0, x: 56 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 32 }}
          transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.9 }}
          className={cn(
            // Phone: overlays the whole canvas; from md up a fixed-width rail.
            'absolute left-2 right-2 top-2 bottom-2 z-30 md:left-auto md:right-4 md:top-4 md:bottom-4 md:w-[372px]',
            'flex flex-col overflow-hidden rounded-2xl',
            SIDEBAR_SURFACE,
            className
          )}
          data-slot="investigation-sidebar"
          data-view={view}
        >
          <SidebarBody caseId={caseId} view={view} onClose={() => onOpenChange(false)} />
        </motion.aside>
      )}
    </AnimatePresence>
  )
}

function SidebarBody({
  caseId,
  view,
  onClose,
}: {
  caseId: string
  view: InvestigationView
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<TabId>('objects')
  const objectSet = useObjectSet(view === 'graph')

  const containerVariants = {
    hidden: {},
    visible: { transition: { delayChildren: 0.08, staggerChildren: 0.03 } },
  }
  const itemVariants = {
    hidden: { opacity: 0, y: 6 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' as const } },
  }

  return (
    <motion.div
      className="flex h-full flex-col"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        variants={itemVariants}
        className="flex items-center justify-between border-b border-border/40 px-4 py-3"
      >
        <div className="flex items-center gap-2">
          {view === 'map' ? (
            <Radar className="size-3.5 text-primary" />
          ) : (
            <Compass className="size-3.5 text-primary" />
          )}
          <span className="text-3xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
            {t('investigations.graph.sidebar.investigation')}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-success shadow-[0_0_6px_hsl(var(--success))]" />
          <span className="font-mono text-4xs uppercase tracking-[0.18em] text-muted-foreground/80">
            {objectSet.ids === null
              ? t('investigations.graph.sidebar.idle')
              : t('investigations.graph.sidebar.active')}
          </span>
          <button
            type="button"
            onClick={onClose}
            title={t('investigations.graph.sidebar.collapse')}
            className="ml-1 inline-flex size-9 items-center justify-center rounded-full text-muted-foreground hover:bg-accent/60 hover:text-foreground md:size-7"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </motion.div>

      <motion.div variants={itemVariants}>
        <SidebarStats view={view} />
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="flex items-center gap-1 border-b border-border/40 px-3 py-2"
      >
        <TabChip
          icon={Layers}
          label={t('investigations.graph.sidebar.tabs.data')}
          count={objectSet.size}
          active={tab === 'objects'}
          onClick={() => setTab('objects')}
        />
        <TabChip
          icon={Sparkles}
          label={t('investigations.graph.sidebar.tabs.insights')}
          active={tab === 'insights'}
          onClick={() => setTab('insights')}
        />
      </motion.div>

      <motion.div variants={itemVariants} className="flex min-h-0 flex-1 flex-col">
        {tab === 'objects' && <ObjectSetTab caseId={caseId} view={view} />}
        {tab === 'insights' && <InsightsTab caseId={caseId} view={view} />}
      </motion.div>
    </motion.div>
  )
}

function SidebarStats({ view }: { view: InvestigationView }) {
  const { t } = useTranslation()
  const objectSet = useObjectSet(view === 'graph')
  const breachAlerts = useGeofenceStore(s => s.breachAlerts)
  const timeAwareCount = useGeolocationStore(s => {
    const c = classifyEntities(s.entities)
    return c.animated.length + c.timeAnchored.length
  })

  if (view === 'map') {
    return (
      <div className="grid grid-cols-3 border-b border-border/40 bg-muted/20">
        <Stat label={t('investigations.graph.sidebar.stats.total')} value={objectSet.size} />
        <Stat
          label={t('investigations.graph.sidebar.stats.trackable')}
          value={timeAwareCount}
          accent
        />
        <Stat
          label={t('investigations.graph.sidebar.stats.breaches')}
          value={breachAlerts.length}
          alert={breachAlerts.length > 0}
        />
      </div>
    )
  }
  return (
    <div className="grid grid-cols-3 border-b border-border/40 bg-muted/20">
      <Stat label={t('investigations.graph.sidebar.stats.total')} value={objectSet.size} />
      <Stat
        label={t('investigations.graph.sidebar.stats.source')}
        value={objectSet.source ? objectSet.source.slice(0, 6).toUpperCase() : '—'}
        textual
      />
      <Stat label={t('investigations.graph.sidebar.stats.selected')} value={0} />
    </div>
  )
}

function ObjectSetTab({ caseId, view }: { caseId: string; view: InvestigationView }) {
  const { t } = useTranslation()
  const objectSet = useObjectSet(view === 'graph')
  const [searchQuery, setSearchQuery] = useState('')
  const [filter, setFilter] = useState<EntityFilter>('all')
  const [openSection, setOpenSection] = useState<SidebarSection>('entities')

  const geofences = useGeofenceStore(s => s.geofences)
  const removeGeofence = useGeofenceStore(s => s.removeGeofence)
  const toggleGeofenceVisibility = useGeofenceStore(s => s.toggleGeofenceVisibility)

  if (objectSet.ids === null) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center">
        <Compass className="size-5 text-muted-foreground/60" />
        <p className="font-mono text-3xs uppercase tracking-[0.18em] text-muted-foreground">
          {t('investigations.graph.sidebar.noActiveInvestigation')}
        </p>
        <p className="max-w-[260px] text-2xs leading-snug text-muted-foreground/70">
          {t('investigations.graph.sidebar.askSpectreFilter')}
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="border-b border-border/40 px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-3 size-3.5 text-muted-foreground md:top-2" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={t('investigations.graph.sidebar.searchPlaceholder')}
            className={cn(
              'w-full rounded-md border border-border/40 bg-muted/40',
              // 16px text on a phone: anything smaller makes iOS zoom the page on focus.
              'pl-8 pr-2 py-2 text-base text-foreground placeholder:text-muted-foreground md:py-1.5 md:text-xs',
              'outline-none focus:border-border/70 focus:bg-muted/60'
            )}
          />
        </div>
      </div>

      {view === 'map' ? (
        <MapObjectSetContent
          caseId={caseId}
          filter={filter}
          setFilter={setFilter}
          searchQuery={searchQuery}
          openSection={openSection}
          setOpenSection={setOpenSection}
          geofences={geofences}
          onToggleGeofence={toggleGeofenceVisibility}
          onRemoveGeofence={removeGeofence}
        />
      ) : (
        <NodeObjectSetContent caseId={caseId} searchQuery={searchQuery} view={view} />
      )}
    </>
  )
}

function MapObjectSetContent({
  caseId,
  filter,
  setFilter,
  searchQuery,
  openSection,
  setOpenSection,
  geofences,
  onToggleGeofence,
  onRemoveGeofence,
}: {
  caseId: string
  filter: EntityFilter
  setFilter: (f: EntityFilter) => void
  searchQuery: string
  openSection: SidebarSection
  setOpenSection: (s: SidebarSection) => void
  geofences: ReturnType<typeof useGeofenceStore.getState>['geofences']
  onToggleGeofence: (id: string) => void
  onRemoveGeofence: (id: string) => void
}) {
  const { t } = useTranslation()
  const entities = useGeolocationStore(s => s.entities)
  const selectedEntityId = useGeolocationStore(s => s.selectedEntityId)
  const selectEntity = useGeolocationStore(s => s.selectEntity)
  const objectSet = useObjectSet()
  // GeoEntity.id is `entity_key` (a stable cross-system label) but Object Set
  // ids are Neo4j node ids — translate via timeline-store's mapping (the
  // maplibre viewer applies the same hop). Without this, every geo entity is
  // filtered out the moment an investigation is active.
  const entityKeyToNodeId = useTimelineStore(s => s.entityKeyToNodeId)

  const inObjectSet = useMemo(() => {
    if (objectSet.ids === null) return entities
    return entities.filter(e => {
      const nodeId = mapObjectSetId(e.id, entityKeyToNodeId) ?? e.id
      return objectSet.memberIds?.has(nodeId) ?? false
    })
  }, [entities, objectSet.ids, objectSet.memberIds, entityKeyToNodeId])

  const { animated, timeAnchored, staticEntities } = useMemo(
    () => classifyEntities(inObjectSet),
    [inObjectSet]
  )
  const filteredAnimated = useMemo(
    () => filterEntities(animated, searchQuery),
    [animated, searchQuery]
  )
  const filteredTimeAnchored = useMemo(
    () => filterEntities(timeAnchored, searchQuery),
    [timeAnchored, searchQuery]
  )
  const filteredStatic = useMemo(
    () => filterEntities(staticEntities, searchQuery),
    [staticEntities, searchQuery]
  )
  const totalShown = filteredAnimated.length + filteredTimeAnchored.length + filteredStatic.length

  return (
    <>
      <div className="flex flex-wrap items-center gap-1 border-b border-border/40 px-3 py-2">
        <FilterChip
          label={t('investigations.graph.sidebar.filters.all')}
          count={inObjectSet.length}
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <FilterChip
          label={t('investigations.graph.sidebar.filters.animated')}
          count={animated.length}
          active={filter === 'animated'}
          onClick={() => setFilter('animated')}
        />
        <FilterChip
          label={t('investigations.graph.sidebar.filters.timeAnchored')}
          count={timeAnchored.length}
          active={filter === 'time-anchored'}
          onClick={() => setFilter('time-anchored')}
        />
        <FilterChip
          label={t('investigations.graph.sidebar.filters.static')}
          count={staticEntities.length}
          active={filter === 'static'}
          onClick={() => setFilter('static')}
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <SectionHeader
          icon={<Layers className="size-3.5 text-muted-foreground" />}
          label={t('investigations.graph.sidebar.sections.entities')}
          count={totalShown}
          open={openSection === 'entities'}
          onClick={() => setOpenSection('entities')}
        />
        {openSection === 'entities' && (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="p-1.5">
              {(filter === 'all' || filter === 'animated') && filteredAnimated.length > 0 && (
                <SubGroup
                  label={t('investigations.graph.sidebar.sections.animated')}
                  count={filteredAnimated.length}
                >
                  {filteredAnimated.map(e => (
                    <EntityRow
                      key={e.id}
                      caseId={caseId}
                      entity={e}
                      nodeId={entityKeyToNodeId[e.id] ?? null}
                      selected={e.id === selectedEntityId}
                      onClick={() => selectEntity(e.id)}
                      showTrackControls
                    />
                  ))}
                </SubGroup>
              )}
              {(filter === 'all' || filter === 'time-anchored') &&
                filteredTimeAnchored.length > 0 && (
                  <SubGroup
                    label={t('investigations.graph.sidebar.sections.timeAnchored')}
                    count={filteredTimeAnchored.length}
                  >
                    {filteredTimeAnchored.map(e => (
                      <EntityRow
                        key={e.id}
                        caseId={caseId}
                        entity={e}
                        nodeId={entityKeyToNodeId[e.id] ?? null}
                        selected={e.id === selectedEntityId}
                        onClick={() => selectEntity(e.id)}
                      />
                    ))}
                  </SubGroup>
                )}
              {(filter === 'all' || filter === 'static') && filteredStatic.length > 0 && (
                <SubGroup
                  label={t('investigations.graph.sidebar.sections.staticInfra')}
                  count={filteredStatic.length}
                >
                  {filteredStatic.map(e => (
                    <EntityRow
                      key={e.id}
                      caseId={caseId}
                      entity={e}
                      nodeId={entityKeyToNodeId[e.id] ?? null}
                      selected={e.id === selectedEntityId}
                      onClick={() => selectEntity(e.id)}
                    />
                  ))}
                </SubGroup>
              )}
              {totalShown === 0 && <ObjectSetEmptyState query={searchQuery} caseId={caseId} />}
            </div>
          </div>
        )}

        {geofences.length > 0 && (
          <SectionHeader
            icon={<Hexagon className="size-3.5 text-muted-foreground" />}
            label={t('investigations.graph.sidebar.sections.geofences')}
            count={geofences.length}
            open={openSection === 'geofences'}
            onClick={() => setOpenSection('geofences')}
          />
        )}
        {openSection === 'geofences' && geofences.length > 0 && (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="space-y-1 p-1.5">
              {geofences.map(g => (
                <GeofenceRow
                  key={g.id}
                  fence={g}
                  onFocus={() => {
                    const target = geofenceMapTarget(g.vertices)
                    if (target) useCockpitStore.getState().setMapTarget(target)
                  }}
                  onToggle={() => onToggleGeofence(g.id)}
                  onRemove={() => onRemoveGeofence(g.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

function NodeObjectSetContent({
  caseId,
  searchQuery,
  view,
}: {
  caseId: string
  searchQuery: string
  view: InvestigationView
}) {
  const { t } = useTranslation()
  const transient = useInvestigationsStore(s => s.transient)
  const activeId = useInvestigationsStore(s => s.activeId)
  const active = useInvestigationsStore(s =>
    activeId ? (s.saved.find(i => i.id === activeId) ?? null) : null
  )
  const graphView = transient?.graphView ?? active?.graphView
  // Saved face evidence is hidden in both views while AFD is off (ADR-0051).
  const { enabled: faceDetection } = useExtensionEnabled(ADVANCED_FACE_DETECTION_EXTENSION_ID)
  // What an analyst marked no longer valid or wrong is listed only with the switch on, greyed (R-C4).
  const [showNoLongerValid] = useShowNoLongerValid()
  const { nodes, edges } = useMemo(() => {
    const nodes = [...(graphView?.nodes ?? []), ...snippetGraphNodes(graphView?.snippets ?? [])]
    const evidence = investigationEvidence(nodes, graphView?.edges ?? [], {
      graphOnly: view === 'graph',
      expandedFaceIds: graphView?.view.expandedFaceIds,
      faceDetection,
    })
    return applyFactVisibility(evidence.nodes, evidence.edges, showNoLongerValid)
  }, [graphView, view, faceDetection, showNoLongerValid])
  const selectedIds = useSelectionStore(s => s.selectedEntityIds)
  const selectedEdgeId = useSelectionStore(s => s.selectedEdgeId)

  // `openSection` follows the canonical selection by default:
  //   - selectedEdgeId set    → 'relationships'
  //   - selectedEntityIds set → 'entities'
  // The user can override that with a manual SectionHeader click; the
  // override stays sticky until the next selection change clears it.
  const [manualOverride, setManualOverride] = useState<'entities' | 'relationships' | null>(null)

  // The "selection key" changes whenever either canonical selection changes.
  // We clear the manual override when it does so the next click reverts to
  // auto-follow behaviour.
  const selectionKey = selectedEdgeId
    ? `edge:${selectedEdgeId}`
    : selectedIds.size > 0
      ? 'entity'
      : 'none'
  const lastSelectionKeyRef = useRef(selectionKey)
  useEffect(() => {
    if (lastSelectionKeyRef.current !== selectionKey) {
      lastSelectionKeyRef.current = selectionKey
      setManualOverride(null)
    }
  }, [selectionKey])

  const autoSection: 'entities' | 'relationships' = selectedEdgeId ? 'relationships' : 'entities'
  const openSection = manualOverride ?? autoSection
  const setOpenSection = setManualOverride

  const nameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const n of nodes) {
      map.set(n.id, getEntityDisplayLabel(n) || n.id)
    }
    return map
  }, [nodes])

  // Pre-resolve display labels so search matches the same string the user
  // sees, and selection toggling stays cheap.
  const enrichedNodes = useMemo(
    () =>
      nodes.map(n => ({
        id: n.id,
        label: getEntityDisplayLabel(n),
        sublabel: (n.labels ?? []).join(' · ') || n.typeLabel || '',
        hasGeo: hasGeoCoordinates(n.properties),
        isPerson: n.typeLabel === 'Person' || Boolean(n.labels?.includes('Person')),
        uid: entityUid(n.properties ?? {}),
        faded: isHiddenFact(n.properties),
      })),
    [nodes]
  )
  // The "changed" badge (PRD-0075 R-C2): merged or a property changed in the last 30 days.
  const nodeUids = useMemo(
    () => enrichedNodes.flatMap(n => (n.uid ? [n.uid] : [])),
    [enrichedNodes]
  )
  const changedUids = useChangedEntities(caseId, nodeUids)

  const enrichedEdges = useMemo(
    () =>
      edges.map(e => {
        const sourceName = nameById.get(e.source) ?? e.source
        const targetName = nameById.get(e.target) ?? e.target
        return {
          id: e.id,
          type: e.type,
          source: e.source,
          target: e.target,
          sourceName,
          targetName,
          properties: e.properties,
        }
      }),
    [edges, nameById]
  )

  const q = searchQuery.trim().toLowerCase()
  const filteredNodes = q
    ? enrichedNodes.filter(
        n =>
          n.label.toLowerCase().includes(q) ||
          n.sublabel.toLowerCase().includes(q) ||
          n.id.toLowerCase().includes(q)
      )
    : enrichedNodes

  const filteredEdges = q
    ? enrichedEdges.filter(
        e =>
          e.type.toLowerCase().includes(q) ||
          e.sourceName.toLowerCase().includes(q) ||
          e.targetName.toLowerCase().includes(q)
      )
    : enrichedEdges

  if (filteredNodes.length === 0 && filteredEdges.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center">
        <p className="font-mono text-3xs uppercase tracking-[0.18em] text-muted-foreground">
          {q
            ? t('investigations.graph.sidebar.noMatches')
            : t('investigations.graph.sidebar.investigationEmpty')}
        </p>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SectionHeader
        icon={<Layers className="size-3.5 text-muted-foreground" />}
        label={t('investigations.graph.sidebar.sections.entities')}
        count={filteredNodes.length}
        open={openSection === 'entities'}
        onClick={() => setOpenSection(openSection === 'entities' ? 'relationships' : 'entities')}
      />
      {openSection === 'entities' && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-0.5 p-1.5">
            {filteredNodes.map(n => (
              <NodeRow
                key={n.id}
                caseId={caseId}
                node={n}
                selected={selectedIds.has(n.id)}
                changed={n.uid !== null && changedUids.has(n.uid)}
              />
            ))}
          </div>
        </div>
      )}

      <SectionHeader
        icon={<GitFork className="size-3.5 text-muted-foreground" />}
        label={t('investigations.graph.sidebar.sections.relationships', 'Relationships')}
        count={filteredEdges.length}
        open={openSection === 'relationships'}
        onClick={() =>
          setOpenSection(openSection === 'relationships' ? 'entities' : 'relationships')
        }
      />
      {openSection === 'relationships' && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-0.5 p-1.5">
            {filteredEdges.length === 0 ? (
              <p className="px-3 py-4 text-center text-2xs text-muted-foreground">
                {t(
                  'investigations.graph.sidebar.sections.relationshipsEmpty',
                  'No relationships on the graph yet.'
                )}
              </p>
            ) : (
              filteredEdges.map(e => (
                <RelationshipRow
                  key={e.id}
                  caseId={caseId}
                  edge={e}
                  selected={e.id === selectedEdgeId}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Shared inline-expand behaviour for entity rows.
//
/**
 * Scroll the row's nearest `overflow-y: auto/scroll` ancestor so the row sits
 * at its top, leaving the expanded detail panel below with maximum space.
 *
 * Why not `Element.scrollIntoView({ block: 'start' })`? That walks the full
 * ancestor chain and scrolls EVERY scroll-container it finds — including
 * `overflow: hidden` wrappers. Even though those don't display scrollbars,
 * their `scrollTop` is still programmatically mutable, and the browser will
 * happily bump it. When the user has the canvas zoomed in and clicks a node,
 * the matched row is far from the sidebar's current scroll position, so
 * `scrollIntoView` walks further up than usual and ends up nudging the
 * `overflow-hidden` workspace wrappers — which visibly shifts the graph +
 * sidebar upward and leaves a blank strip below.
 *
 * Scrolling only the explicitly-marked scroll container keeps the page
 * layout pinned and matches the user's mental model of "the list scrolls,
 * nothing else moves".
 */
function scrollRowToTopOfList(row: HTMLElement) {
  let container: HTMLElement | null = row.parentElement
  while (container) {
    const overflowY = window.getComputedStyle(container).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') break
    container = container.parentElement
  }
  if (!container) return
  const delta = row.getBoundingClientRect().top - container.getBoundingClientRect().top
  if (Math.abs(delta) < 1) return
  container.scrollBy({ top: delta, behavior: 'smooth' })
}

// Expansion is purely derived from the canonical selection-store: a row is
// expanded iff its `entityNodeId` is in `selectedEntityIds`. Clicking the
// header writes a single-id replacement (so any other previously-expanded row
// collapses), or clears it when the row is already expanded. This guarantees
// at most one entity-detail body is mounted at a time across the sidebar.
function useExpandableEntityRow({
  entityNodeId,
  onSelect,
}: {
  entityNodeId: string | null
  onSelect?: () => void
}) {
  const expanded = useSelectionStore(s =>
    entityNodeId ? s.selectedEntityIds.has(entityNodeId) : false
  )
  const replaceSelection = useSelectionStore(s => s.replace)
  const rowRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (expanded) {
      // Defer to the next frame so the detail panel mounts before we scroll.
      requestAnimationFrame(() => {
        if (rowRef.current) scrollRowToTopOfList(rowRef.current)
      })
    }
  }, [expanded])

  const onHeaderClick = useCallback(() => {
    if (!entityNodeId) return
    if (expanded) {
      // Collapse — clear the canonical selection so any other view's mirroring
      // hook can settle. We deliberately skip `onSelect` here so the caller's
      // selection plumbing doesn't immediately re-add this id and re-expand
      // the row.
      replaceSelection([], 'graph')
      return
    }
    replaceSelection([entityNodeId], 'graph')
    onSelect?.()
  }, [entityNodeId, expanded, replaceSelection, onSelect])

  return { rowRef, expanded, onHeaderClick }
}

function NodeRow({
  caseId,
  node,
  selected,
  changed = false,
}: {
  caseId: string
  node: {
    id: string
    label: string
    sublabel: string
    hasGeo: boolean
    isPerson: boolean
    /** Marked wrong, shown by the "no longer valid" switch (PRD-0075 R-C4). */
    faded?: boolean
  }
  selected?: boolean
  /** Merged or a property changed in the last 30 days (PRD-0075 R-C2). */
  changed?: boolean
}) {
  const { id, label: name, sublabel, hasGeo, isPerson } = node
  const { t } = useTranslation()
  const typeColor = pastelFor(sublabel ?? id)
  const { rowRef, expanded, onHeaderClick } = useExpandableEntityRow({ entityNodeId: id })

  return (
    <div
      ref={rowRef}
      className={cn(
        'group rounded-md transition-colors',
        selected || expanded ? 'bg-primary/10 ring-1 ring-primary/40' : 'hover:bg-accent/50',
        node.faded && 'opacity-50'
      )}
    >
      <div className="flex items-center rounded-md py-2 pl-3 pr-2.5">
        <button
          type="button"
          onClick={onHeaderClick}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          {snippetSelection(id) ? (
            <Table2 className="size-4 shrink-0 text-muted-foreground" />
          ) : (
            <span
              className="size-2.5 flex-shrink-0 rounded-full ring-1 ring-black/30"
              style={{ background: typeColor }}
            />
          )}
          <div className="min-w-0 flex-1 overflow-hidden">
            <div className="flex items-center gap-1.5">
              <p
                className={cn(
                  'overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium',
                  selected || expanded ? 'text-primary' : 'text-foreground/85'
                )}
              >
                {name}
              </p>
              {changed && <ChangedBadge />}
              {hasGeo && (
                <MapPin
                  className="size-3 shrink-0 text-success"
                  aria-label={t('investigations.entityDetail.hasLocation')}
                />
              )}
            </div>
            {sublabel && (
              <p className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-3xs tabular-nums text-muted-foreground">
                {sublabel}
              </p>
            )}
          </div>
        </button>
        {isPerson && (
          <div className="mx-2 flex shrink-0 items-center">
            <OpenIdentityLink caseId={caseId} person={{ id, name }} />
          </div>
        )}
        <CopyNameButton name={name} />
        <ChevronDown
          className={cn(
            'ml-1 size-3.5 shrink-0 text-muted-foreground transition-transform',
            expanded ? 'rotate-0 text-foreground' : '-rotate-90'
          )}
        />
      </div>
      {expanded && (
        <SidebarEntityDetail caseId={caseId} nodeId={id} presentation={{ identityLink: false }} />
      )}
    </div>
  )
}

// Compact icon button that copies the entity name to the clipboard, briefly
// swapping to a check icon for feedback. Stays hidden until the row is hovered
// so it doesn't clutter the list.
function CopyNameButton({ name }: { name: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(name)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error(t('investigations.sidebar.copyNameError', 'Could not copy name'))
    }
  }, [name, t])

  return (
    <button
      type="button"
      onClick={onCopy}
      title={t('investigations.sidebar.copyName', 'Copy name')}
      aria-label={t('investigations.sidebar.copyName', 'Copy name')}
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded-sm transition-opacity',
        // No hover on touch screens: keep the button visible and tappable.
        '[@media(hover:none)]:size-8 [@media(hover:none)]:opacity-100',
        copied
          ? 'text-primary opacity-100'
          : 'text-muted-foreground opacity-0 hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100'
      )}
    >
      {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
    </button>
  )
}

function RelationshipRow({
  caseId,
  edge,
  selected,
}: {
  caseId: string
  edge: {
    id: string
    type: string
    source: string
    target: string
    sourceName: string
    targetName: string
    properties: Record<string, unknown>
  }
  selected: boolean
}) {
  const selectEdge = useSelectionStore(s => s.selectEdge)
  const rowRef = useRef<HTMLDivElement | null>(null)
  const expanded = selected
  const isPossibleMatch = edge.type === 'POSSIBLE_MATCH'

  useEffect(() => {
    if (expanded) {
      // Match the entity row behaviour: align to the top so the inline detail
      // panel below has the maximum visible space. See `scrollRowToTopOfList`
      // for why we don't use the native `Element.scrollIntoView`.
      requestAnimationFrame(() => {
        if (rowRef.current) scrollRowToTopOfList(rowRef.current)
      })
    }
  }, [expanded])

  const selectedEdge = useMemo(
    () => ({
      id: edge.id,
      type: edge.type,
      source: edge.source,
      target: edge.target,
      sourceName: edge.sourceName,
      targetName: edge.targetName,
      properties: edge.properties,
    }),
    [edge]
  )

  return (
    <div
      ref={rowRef}
      className={cn(
        'rounded-md transition-colors',
        expanded
          ? isPossibleMatch
            ? 'bg-warning/10 ring-1 ring-warning/50'
            : 'bg-primary/10 ring-1 ring-primary/40'
          : 'hover:bg-accent/50',
        isHiddenFact(edge.properties) && 'opacity-50'
      )}
    >
      <button
        type="button"
        onClick={() => selectEdge(expanded ? null : edge.id, 'graph')}
        aria-expanded={expanded}
        className="group flex w-full items-center gap-2 rounded-md py-2 pl-3 pr-2.5 text-left"
      >
        <span
          className={cn(
            'inline-flex h-5 shrink-0 items-center rounded-md border px-1.5 text-4xs font-semibold uppercase tracking-wider',
            isPossibleMatch
              ? 'border-warning/50 bg-warning/15 text-warning'
              : 'border-border/60 bg-muted/60 text-muted-foreground'
          )}
        >
          {edge.type}
        </span>
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex items-center gap-1 text-xs text-foreground/85">
            <span className="truncate" title={edge.sourceName}>
              {edge.sourceName}
            </span>
            <ArrowRight className="size-3 shrink-0 text-muted-foreground" />
            <span className="truncate" title={edge.targetName}>
              {edge.targetName}
            </span>
          </div>
        </div>
        <ChevronDown
          className={cn(
            'size-3.5 shrink-0 text-muted-foreground transition-transform',
            expanded ? 'rotate-0 text-foreground' : '-rotate-90'
          )}
        />
      </button>
      {expanded && <SidebarRelationshipDetail caseId={caseId} selectedEdge={selectedEdge} />}
    </div>
  )
}

function InsightsTab({ caseId, view }: { caseId: string; view: InvestigationView }) {
  const { t } = useTranslation()
  // Phase 1: AI Insights only support the `graph` lens server-side. The
  // structural-analysis content (Louvain / bridges / leaderboard) that this
  // tab used to render moved to the Explore tab.
  if (view !== 'graph') {
    return (
      <div className="px-4 py-6 text-center">
        <p className="text-2xs text-muted-foreground">
          {t('investigations.graph.sidebar.insightsComingSoon', { view })}
        </p>
      </div>
    )
  }
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-3">
      <AiInsightsPanel caseId={caseId} lens="graph" />
    </div>
  )
}

// Legacy structural-analysis tab body — kept around so the Explore tab work
// in §8 can pick it up and import. References to `useInsights`,
// `resolveInsight`, `insightView`, `Insight`, and the legacy `InsightCard`
// are deliberately retained.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _LegacyStructuralInsightsTab({
  caseId,
  view,
}: {
  caseId: string
  view: InvestigationView
}) {
  const { insights, isLoading } = useInsights(caseId)
  const transient = useInvestigationsStore(s => s.transient)
  const activeId = useInvestigationsStore(s => s.activeId)
  const storeApi = useInvestigationsStoreApi()
  const [resolvingId, setResolvingId] = useState<string | null>(null)
  const fromInsightId = transient?.fromInsightId

  const filtered = useMemo(() => {
    const matching = insights.filter(i => insightView(i) === view)
    if (matching.length > 0) return matching
    return insights
  }, [insights, view])

  if (isLoading && insights.length === 0) {
    return (
      <div className="px-4 py-6 text-center">
        <p className="font-mono text-3xs uppercase tracking-[0.18em] text-muted-foreground">
          Computing insights…
        </p>
      </div>
    )
  }
  if (filtered.length === 0) {
    return (
      <div className="px-4 py-6 text-center">
        <p className="text-2xs text-muted-foreground">No {view} insights yet.</p>
      </div>
    )
  }
  const onView = async (insight: Insight) => {
    setResolvingId(insight.id)
    try {
      const v = await resolveInsight(caseId, insight)
      const label = `Insight · ${insight.kind}`
      const result = storeApi.getState().addToObjectSet(v, label, 'insight')
      if (result.addedNodeIds.length > 0 || result.addedEdgeIds.length > 0) {
        const state = storeApi.getState()
        const investigationKey = state.activeId ?? (state.transient ? 'transient' : null)
        if (investigationKey) {
          useObjectSetContributionsStore.getState().add({
            id: result.contributionId,
            investigationKey,
            label,
            source: 'insight',
            nodeIds: result.addedNodeIds,
            edgeIds: result.addedEdgeIds,
          })
        }
      }
    } finally {
      setResolvingId(null)
    }
  }
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <ul className="flex flex-col gap-2 p-3">
        {filtered.map(ins => (
          <li key={ins.id} className="w-full">
            <InsightCard
              insight={ins}
              active={fromInsightId === ins.id || activeId === ins.id}
              loading={resolvingId === ins.id}
              onClick={() => onView(ins)}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

function Stat({
  label,
  value,
  accent,
  alert,
  textual,
}: {
  label: string
  value: number | string
  accent?: boolean
  alert?: boolean
  textual?: boolean
}) {
  return (
    <div className="px-3 py-2">
      <div className="text-4xs font-medium uppercase tracking-[0.18em] text-muted-foreground/80">
        {label}
      </div>
      <div
        className={cn(
          'font-mono leading-tight tabular-nums',
          textual ? 'text-xs' : 'text-lg',
          accent ? 'text-primary' : alert ? 'text-warning' : 'text-foreground'
        )}
      >
        {value}
      </div>
    </div>
  )
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-2 md:py-1',
        'border text-3xs uppercase tracking-[0.1em] transition-colors',
        active
          ? 'border-primary/55 bg-primary/15 text-primary'
          : 'border-border/40 bg-card/40 text-muted-foreground hover:bg-accent/60 hover:text-foreground'
      )}
    >
      <span className="font-medium">{label}</span>
      <span
        className={cn(
          'font-mono tabular-nums',
          active ? 'text-primary/85' : 'text-muted-foreground/70'
        )}
      >
        {count}
      </span>
    </button>
  )
}

function TabChip({
  icon: Icon,
  label,
  count,
  active,
  onClick,
}: {
  icon: LucideIcon
  label: string
  count?: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-2.5 md:py-1.5',
        'border text-3xs uppercase tracking-[0.14em] transition-colors',
        active
          ? 'border-primary/55 bg-primary/15 text-primary'
          : 'border-border/40 bg-card/40 text-muted-foreground hover:bg-accent/60 hover:text-foreground'
      )}
    >
      <Icon className="size-3" />
      <span className="font-medium">{label}</span>
      {count !== undefined && (
        <span
          className={cn(
            'font-mono tabular-nums',
            active ? 'text-primary/85' : 'text-muted-foreground/70'
          )}
        >
          {count}
        </span>
      )}
    </button>
  )
}

function SectionHeader({
  icon,
  label,
  count,
  open,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  count: number
  open: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 border-b border-border/40 px-3 py-3 text-left md:py-2',
        'transition-colors hover:bg-accent/40'
      )}
    >
      <ChevronRight
        className={cn(
          'size-3 flex-shrink-0 text-muted-foreground transition-transform',
          open && 'rotate-90'
        )}
      />
      {icon}
      <span className="text-3xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </span>
      <span className="ml-auto inline-flex items-center gap-1.5">
        <span className="rounded border border-border/50 px-1.5 py-0.5 font-mono text-3xs tabular-nums text-muted-foreground">
          {count}
        </span>
      </span>
    </button>
  )
}

function SubGroup({
  label,
  count,
  children,
}: {
  label: string
  count: number
  children: React.ReactNode
}) {
  return (
    <div className="mb-1">
      <div className="flex items-center gap-1.5 px-2 pt-2 pb-1">
        <span className="text-4xs font-medium uppercase tracking-[0.18em] text-muted-foreground/60">
          {label}
        </span>
        <span className="font-mono text-4xs tabular-nums text-muted-foreground/60">{count}</span>
        <span className="h-px flex-1 bg-border/40" />
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  )
}

function EntityRow({
  caseId,
  entity,
  nodeId,
  selected,
  onClick,
  showTrackControls = false,
}: {
  caseId: string
  entity: GeoEntity
  nodeId: string | null
  selected: boolean
  onClick: () => void
  showTrackControls?: boolean
}) {
  const trackColors = useGeolocationStore(s => s.trackColors)
  const setEntityTrackColor = useGeolocationStore(s => s.setEntityTrackColor)
  const hiddenEntityIds = useGeolocationStore(s => s.hiddenEntityIds)
  const toggleEntityVisibility = useGeolocationStore(s => s.toggleEntityVisibility)
  const tracks = useGeolocationStore(s => s.tracks)

  const typeColor = pastelFor(entity.label ?? entity.type)
  const customTrackColor = trackColors[entity.id]
  const hasTrackData = (tracks[entity.id]?.length ?? 0) >= 2
  const renderTrackControls = showTrackControls && hasTrackData
  const entityHidden = hiddenEntityIds.has(entity.id)
  // Route the display label through the shared resolver so SocialMediaPost
  // (and any other content-bearing entity) gets a useful snippet rather than
  // "Unknown" when the backend left `name` blank or placeholder.
  const displayLabel = getEntityDisplayLabel({
    id: entity.id,
    name: entity.name,
    typeLabel: entity.label,
    properties: entity.properties,
  })
  // Swatch tracks the line drawn on the map: explicit override first,
  // otherwise the same per-entity palette colour the map line uses by
  // default. (`typeColor` stays for the small pin-type indicator dot.)
  const swatchColor = customTrackColor ?? defaultTrackColorFor(entity.id)

  const { rowRef, expanded, onHeaderClick } = useExpandableEntityRow({
    entityNodeId: nodeId,
    onSelect: onClick,
  })

  return (
    <div
      ref={rowRef}
      className={cn(
        'rounded-md transition-colors',
        selected || expanded ? 'bg-primary/10 ring-1 ring-primary/40' : 'hover:bg-accent/50'
      )}
    >
      <div className="group relative flex w-full items-center gap-3 py-2 pl-3 pr-2.5">
        <button
          type="button"
          onClick={onHeaderClick}
          aria-expanded={expanded}
          title={displayLabel}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <span
            className="size-2.5 flex-shrink-0 rounded-full ring-1 ring-black/30"
            style={{ background: typeColor }}
          />
          <div className="min-w-0 flex-1 overflow-hidden">
            <p
              className={cn(
                'overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium',
                selected || expanded ? 'text-foreground' : 'text-foreground/85'
              )}
            >
              {displayLabel}
            </p>
            <p className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-3xs tabular-nums text-muted-foreground">
              {entity.type} · {entity.latitude.toFixed(3)}, {entity.longitude.toFixed(3)}
            </p>
          </div>
          <ChevronDown
            className={cn(
              'size-3.5 shrink-0 text-muted-foreground transition-transform',
              expanded ? 'rotate-0 text-foreground' : '-rotate-90'
            )}
          />
        </button>
        <div className="flex flex-shrink-0 items-center gap-1">
          {renderTrackControls && (
            <TrackColorPicker
              value={customTrackColor ?? null}
              swatchColor={swatchColor}
              onChange={c => setEntityTrackColor(entity.id, c)}
            />
          )}
          <button
            type="button"
            onClick={() => toggleEntityVisibility(entity.id)}
            title={entityHidden ? 'Show on map' : 'Hide from map'}
            className={cn(
              'flex size-5 items-center justify-center rounded-sm transition-opacity',
              '[@media(hover:none)]:size-8 [@media(hover:none)]:opacity-100',
              entityHidden || selected
                ? 'opacity-100 text-foreground/85 hover:text-foreground'
                : 'opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground'
            )}
          >
            {entityHidden ? <EyeOff className="size-3" /> : <Eye className="size-3" />}
          </button>
        </div>
      </div>
      {expanded ? (
        nodeId ? (
          <SidebarEntityDetail caseId={caseId} nodeId={nodeId} />
        ) : (
          <div className="border-t border-border/40 px-3 py-3 text-2xs text-muted-foreground">
            No graph node mapped for this entity yet.
          </div>
        )
      ) : null}
    </div>
  )
}

function TrackColorPicker({
  value,
  swatchColor,
  onChange,
}: {
  value: string | null
  swatchColor: string
  onChange: (color: string | null) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={t('investigations.graph.sidebar.trackColor')}
          className={cn(
            'relative flex size-8 items-center justify-center rounded-sm md:size-5',
            'hover:bg-accent/60 transition-colors'
          )}
        >
          <span
            className="size-3 rounded-full ring-1 ring-black/30"
            style={{ background: swatchColor }}
          />
          {value == null && (
            <Palette className="absolute -bottom-0.5 -right-0.5 size-2.5 text-muted-foreground/70" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="center"
        sideOffset={8}
        className={cn(
          'w-[224px] rounded-xl border-border/40 p-2 md:w-[176px]',
          'bg-popover/95 backdrop-blur-xl backdrop-saturate-150 text-popover-foreground'
        )}
      >
        <div className="grid grid-cols-6 gap-1.5">
          {TRACK_COLOR_PALETTE.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => {
                onChange(c)
                setOpen(false)
              }}
              className={cn(
                'size-7 rounded-full ring-1 ring-black/40 transition-transform hover:scale-110 md:size-5',
                value === c && 'ring-2 ring-foreground'
              )}
              style={{ background: c }}
              title={c}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => {
            onChange(null)
            setOpen(false)
          }}
          className={cn(
            'mt-2 w-full rounded-md px-2 py-2 text-3xs uppercase tracking-[0.16em] md:py-1',
            'text-muted-foreground hover:bg-accent/60 hover:text-foreground transition-colors'
          )}
        >
          {t('investigations.graph.sidebar.resetToDefault')}
        </button>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Map-target derivation for a geofence: bbox centroid + a zoom that fits the
 * bbox with padding. `log2(360 / span) - 1` follows the 360°/2^z visible-span
 * rule; latitude is cosine-corrected so high-latitude fences don't over-zoom.
 */
function geofenceMapTarget(
  vertices: { latitude: number; longitude: number }[]
): { lat: number; lon: number; zoom: number } | null {
  if (vertices.length === 0) return null
  let minLat = Infinity
  let maxLat = -Infinity
  let minLon = Infinity
  let maxLon = -Infinity
  for (const v of vertices) {
    if (v.latitude < minLat) minLat = v.latitude
    if (v.latitude > maxLat) maxLat = v.latitude
    if (v.longitude < minLon) minLon = v.longitude
    if (v.longitude > maxLon) maxLon = v.longitude
  }
  const lat = (minLat + maxLat) / 2
  const lon = (minLon + maxLon) / 2
  const span = Math.max(maxLat - minLat, (maxLon - minLon) * Math.cos((lat * Math.PI) / 180))
  const zoom = span > 0 ? Math.max(2, Math.min(18, Math.log2(360 / span) - 1)) : 15
  return { lat, lon, zoom }
}

function GeofenceRow({
  fence,
  onFocus,
  onToggle,
  onRemove,
}: {
  fence: {
    id: string
    name: string
    color: string
    visible: boolean
    vertices: unknown[]
    height: number
    infiniteHeight: boolean
  }
  onFocus: () => void
  onToggle: () => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  return (
    <div
      className="group flex items-center gap-2 rounded-md border-l-2 px-2 py-1.5 hover:bg-accent/40"
      style={{ borderLeftColor: fence.color }}
    >
      <button
        type="button"
        onClick={onFocus}
        title="Focus on map"
        className="min-w-0 flex-1 cursor-pointer text-left"
      >
        <p className="truncate text-xs text-foreground/90">{fence.name}</p>
        <p className="font-mono text-3xs text-muted-foreground">
          {fence.vertices.length} pts
          {fence.infiniteHeight ? ' · all alt' : ` · ${fence.height} m`}
        </p>
      </button>
      <button
        type="button"
        onClick={onToggle}
        title={t(
          fence.visible ? 'investigations.graph.sidebar.hide' : 'investigations.graph.sidebar.show'
        )}
        className="text-muted-foreground opacity-0 hover:text-foreground group-hover:opacity-100 [@media(hover:none)]:p-2 [@media(hover:none)]:opacity-100"
      >
        {fence.visible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
      </button>
      <button
        type="button"
        onClick={onRemove}
        title={t('investigations.graph.sidebar.delete')}
        className="text-rose-400 opacity-0 hover:text-rose-300 group-hover:opacity-100 [@media(hover:none)]:p-2 [@media(hover:none)]:opacity-100"
      >
        <Trash2 className="size-3.5" />
      </button>
    </div>
  )
}

function ObjectSetEmptyState({ query, caseId }: { query: string; caseId: string }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center gap-1.5 px-4 py-10 text-center">
      <Radar className="size-5 text-muted-foreground/60" />
      <p className="font-mono text-3xs uppercase tracking-[0.18em] text-muted-foreground">
        {query
          ? t('investigations.graph.sidebar.noMatches')
          : t('investigations.graph.sidebar.noMatchingGeo')}
      </p>
      {!query && (
        <p className="max-w-[230px] text-3xs leading-snug text-muted-foreground/70">
          {t('investigations.graph.sidebar.geoHint')}{' '}
          <Link
            to={`/cases/${caseId}/time-axis`}
            className="text-primary underline decoration-dotted underline-offset-[3px]"
          >
            {t('nav.subviews.timeline')}
          </Link>
          .
        </p>
      )}
    </div>
  )
}

function filterEntities(list: GeoEntity[], q: string): GeoEntity[] {
  const trimmed = q.trim().toLowerCase()
  if (!trimmed) return list
  return list.filter(
    e =>
      e.name.toLowerCase().includes(trimmed) ||
      e.type.toLowerCase().includes(trimmed) ||
      (e.label ?? '').toLowerCase().includes(trimmed)
  )
}
